import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'
import LoadingWithValueProps from '@mortgage-pos/ui/components/LoadingWithValueProps'

import {
  Routes,
  NtlQualResult,
  NtlDisqualifiedReason,
  LoanPurpose,
} from '@mortgage-pos/types'
import { useVariation } from '../services/featureFlags'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'

const NTLQualification = () => {
  const navigate = useNavigate()

  // State
  const { statusesMap, isSplitFlow } = useStoreState(
    (state) => state.questionnaire
  )
  const { mergedAnswers } = useStoreState((state) => state.answers)
  const { softQualificationStatus } = useStoreState((state) => state.noTalkLock)

  // Actions
  const { softPullCredit, updateStatus } = useStoreActions(
    (state) => state.application
  )
  const { setAnswers } = useStoreActions((state) => state.answers)
  const { next, setFinished } = useStoreActions((state) => state.questionnaire)
  const { setRates, requestRates, setDebtToIncome } = useStoreActions(
    (state) => state.rates
  )
  const { getQualificationStatus, disqualify } = useStoreActions(
    (state) => state.noTalkLock
  )

  const skipBorrowerPortal = useVariation(
    FEATURE_FLAG_TYPES.SKIP_BORROWER_PORTAL
  )

  async function handleDisqualify(reason: string) {
    await disqualify(reason)

    // Empty rates before moving to base rates page to ensure escrow and fees not rolled in
    setRates([])
    await next()
  }

  async function handleNoRatesRouting() {
    await updateStatus(statusesMap.noRates)
    setFinished(true)
  }

  useEffect(() => {
    async function determineNTLEligibility() {
      try {
        // Required no matter which rates experience
        // except for NTL split flows where we call in a LoaderWithAPICalls page
        if (!isSplitFlow) {
          await softPullCredit()
        }

        // @ts-ignore
        const ratesResponse = await requestRates()

        if (!ratesResponse.length) {
          return await handleNoRatesRouting()
        }
      } catch (e) {
        return await handleNoRatesRouting()
      }

      // Start NTL specific logic
      if (!softQualificationStatus.isSoftQualified) {
        await handleDisqualify(
          `Failed soft qualification - ${softQualificationStatus.reason}`
        )
        return
      }

      let currentMortgageLoanTerm

      try {
        const {
          isQualified,
          debtToIncome,
          reason,
          currentLoanTerm,
        }: NtlQualResult = await getQualificationStatus()

        if (debtToIncome) {
          setDebtToIncome(Math.floor(debtToIncome))
        }

        if (!isQualified) {
          await handleDisqualify(`Failed NTL qualification - ${reason}`)
          return
        }

        if (currentLoanTerm) {
          currentMortgageLoanTerm = currentLoanTerm
        }

        const ratesResponse = await requestRates(debtToIncome)

        if (!ratesResponse.length) {
          // DQ but allow to continue on regular flow with initial rates
          await handleDisqualify(NtlDisqualifiedReason.NO_RATES)
          return
        }
      } catch (e) {
        await handleDisqualify('NTLQualification - Prequal qual API call error')
        return
      }

      setAnswers({
        isNtlQualified: true,
        currentLoanTerm: currentMortgageLoanTerm,
      })

      updateStatus(statusesMap.rates)

      newRelic.increment('ntl_qualification.success')

      await next()
    }

    determineNTLEligibility()
  }, [])

  return <LoadingWithValueProps />
}

export default NTLQualification
